// src/components/ProductDetailModal.js
import React, { useState, useEffect } from 'react';
import { getPublicStore, getReviews, createReview } from '../api/apiService';
import ProductReviewForm     from './ProductReviewForm';
import ProductReviewsDisplay from './ProductReviewsDisplay';

const ds = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const bs = { fontFamily: "'Montserrat', sans-serif" };

const ProductDetailModal = ({ product, onClose, onAddToCart }) => {
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity,        setQuantity]        = useState(1);
  const [publicStore,     setPublicStore]     = useState(null);
  const [reviews,         setReviews]         = useState([]);
  const [activeImg,       setActiveImg]       = useState(0);
  const [showForm,        setShowForm]        = useState(false);
  const [adding,          setAdding]          = useState(false);
  const [added,           setAdded]           = useState(false);

  useEffect(() => {
    if (!product) return;
    (async () => {
      const [store, allReviews] = await Promise.all([
        getPublicStore(),
        getReviews(),
      ]);
      setPublicStore(store || null);
      setReviews(allReviews.filter(r => r.productId === product.id));
      if (product.variants?.length) setSelectedVariant(product.variants[0]);
    })();
  }, [product]);

  useEffect(() => {
    const esc = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', esc);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  if (!product) return null;

  const stock = (() => {
    if (!selectedVariant || !publicStore) return 0;
    return Number(selectedVariant.storeStocks?.[publicStore.id] ?? 0);
  })();

  const handleVariantSelect = (variant) => {
    setSelectedVariant(variant);
    setQuantity(1);
    setAdded(false);
  };

  const handleAddToCart = async () => {
    if (!selectedVariant || quantity < 1 || stock < quantity) return;
    setAdding(true);
    await new Promise(r => setTimeout(r, 300));
    onAddToCart(product, selectedVariant, quantity);
    setAdding(false);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleReviewSubmit = async (review) => {
    await createReview(review);
    const all = await getReviews();
    setReviews(all.filter(r => r.productId === product.id));
    setShowForm(false);
  };

  const images = product.images || [];

  // Agrupar variantes por talla para mostrar selector visual
  const tallas = [...new Set((product.variants || []).map(v => v.size))];
  const coloresDeTalla = (talla) =>
    (product.variants || []).filter(v => v.size === talla);

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(6,13,20,0.75)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px', overflowY: 'auto' }}>

      <div style={{ background: 'white', width: '100%', maxWidth: '900px',
        maxHeight: '92vh', overflowY: 'auto', position: 'relative',
        boxShadow: '0 40px 100px rgba(0,0,0,0.35)', borderRadius: '2px' }}>

        {/* Cerrar */}
        <button onClick={onClose} style={{
          position: 'absolute', top: '16px', right: '16px', zIndex: 10,
          background: 'white', border: '1px solid #f0eaea', cursor: 'pointer',
          color: '#aaa', padding: '6px', borderRadius: '2px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.2s',
        }}
        onMouseOver={e => { e.currentTarget.style.borderColor = '#961914'; e.currentTarget.style.color = '#961914'; }}
        onMouseOut={e => { e.currentTarget.style.borderColor = '#f0eaea'; e.currentTarget.style.color = '#aaa'; }}>
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}
          className="product-detail-grid">

          {/* ── Galería ──────────────────────────────────────────────── */}
          <div style={{ background: '#f8f5f5', display: 'flex',
            flexDirection: 'column', minHeight: '400px' }}>

            {/* Imagen principal */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center',
              justifyContent: 'center', padding: '32px', minHeight: '300px' }}>
              {images.length > 0 ? (
                <img key={activeImg} src={images[activeImg]} alt={product.name}
                  style={{ maxWidth: '100%', maxHeight: '320px', objectFit: 'contain',
                    animation: 'fadeImg 0.2s ease' }} />
              ) : (
                <div style={{ textAlign: 'center', color: '#ccc' }}>
                  <svg width="56" height="56" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={0.8}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                  </svg>
                  <p style={{ ...bs, fontSize: '10px', letterSpacing: '0.2em',
                    textTransform: 'uppercase', marginTop: '10px' }}>Sin imagen</p>
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div style={{ display: 'flex', gap: '8px', padding: '0 20px 20px',
                overflowX: 'auto' }}>
                {images.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} style={{
                    flexShrink: 0, width: '56px', height: '56px', padding: 0,
                    border: i === activeImg ? '2px solid #961914' : '2px solid transparent',
                    outline: i === activeImg ? 'none' : '1px solid #e5e5e5',
                    cursor: 'pointer', overflow: 'hidden', borderRadius: '1px',
                    background: 'none', transition: 'border-color 0.2s',
                  }}>
                    <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Detalle ───────────────────────────────────────────────── */}
          <div style={{ padding: '32px 28px', overflowY: 'auto' }}>

            {/* Categoría */}
            {product.categoryName && (
              <p style={{ ...bs, color: '#961914', fontSize: '10px',
                letterSpacing: '0.25em', textTransform: 'uppercase', marginBottom: '8px' }}>
                {product.categoryName}
              </p>
            )}

            {/* Nombre */}
            <h2 style={{ ...ds, fontSize: '1.9rem', fontWeight: 600,
              color: '#060D14', lineHeight: 1.2, marginBottom: '10px' }}>
              {product.name}
            </h2>

            {/* Precio */}
            <p style={{ ...ds, color: '#961914', fontSize: '1.7rem',
              fontWeight: 400, marginBottom: '16px' }}>
              ${Number(product.price).toFixed(2)}
              <span style={{ ...bs, fontSize: '11px', color: '#bbb',
                marginLeft: '6px', fontWeight: 400 }}>MXN</span>
            </p>

            {/* Descripción */}
            {product.description && (
              <p style={{ ...bs, color: '#777', fontSize: '13px',
                lineHeight: 1.75, marginBottom: '24px' }}>
                {product.description}
              </p>
            )}

            <div style={{ height: '1px', background: '#f0eaea', marginBottom: '24px' }} />

            {/* ── Selector de talla visual ─────────────────────────── */}
            {tallas.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', marginBottom: '10px' }}>
                  <label style={{ ...bs, fontSize: '10px', letterSpacing: '0.2em',
                    textTransform: 'uppercase', color: '#999' }}>
                    Talla
                  </label>
                  {selectedVariant && (
                    <span style={{ ...bs, fontSize: '11px', color: '#666' }}>
                      Seleccionada: <strong>{selectedVariant.size}</strong>
                      {selectedVariant.color && ` · ${selectedVariant.color}`}
                    </span>
                  )}
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {tallas.map(talla => {
                    const variantes = coloresDeTalla(talla);
                    // Para tallas con un solo color, seleccionar directamente
                    const variant   = variantes[0];
                    const varStock  = publicStore
                      ? (variant?.storeStocks?.[publicStore.id] || 0)
                      : 0;
                    const isSelected = selectedVariant?.size === talla
                      && selectedVariant?.color === variant?.color;
                    const sinStock  = varStock <= 0;

                    return (
                      <button key={talla}
                        onClick={() => !sinStock && handleVariantSelect(variant)}
                        disabled={sinStock}
                        style={{
                          ...bs, width: '52px', height: '52px',
                          display: 'flex', flexDirection: 'column',
                          alignItems: 'center', justifyContent: 'center',
                          border: isSelected ? '2px solid #961914' : '1px solid #e5e0e0',
                          background: isSelected ? '#fdf2f2' : sinStock ? '#fafafa' : 'white',
                          cursor: sinStock ? 'not-allowed' : 'pointer',
                          borderRadius: '2px', transition: 'all 0.15s',
                          opacity: sinStock ? 0.45 : 1, position: 'relative',
                        }}
                        onMouseOver={e => { if (!sinStock && !isSelected) e.currentTarget.style.borderColor = '#961914'; }}
                        onMouseOut={e => { if (!isSelected) e.currentTarget.style.borderColor = '#e5e0e0'; }}>
                        <span style={{ fontSize: '12px', fontWeight: 700,
                          color: isSelected ? '#961914' : sinStock ? '#bbb' : '#060D14' }}>
                          {talla}
                        </span>
                        {/* Punto de disponibilidad */}
                        <div style={{ width: '5px', height: '5px', borderRadius: '50%',
                          background: sinStock ? '#e5e0e0' : varStock <= 5 ? '#f59e0b' : '#22c55e',
                          marginTop: '3px' }} />
                        {/* Cruz si sin stock */}
                        {sinStock && (
                          <div style={{ position: 'absolute', top: '50%', left: '50%',
                            transform: 'translate(-50%,-50%) rotate(45deg)',
                            width: '1px', height: '70%', background: '#e0d8d8' }} />
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Leyenda */}
                <div style={{ display: 'flex', gap: '14px', marginTop: '10px' }}>
                  {[
                    { color: '#22c55e', label: 'Disponible' },
                    { color: '#f59e0b', label: 'Pocas piezas' },
                    { color: '#e5e0e0', label: 'Sin stock' },
                  ].map(l => (
                    <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <div style={{ width: '7px', height: '7px', borderRadius: '50%',
                        background: l.color }} />
                      <span style={{ ...bs, fontSize: '10px', color: '#bbb' }}>{l.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Stock del seleccionado */}
            {selectedVariant && publicStore && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px',
                marginBottom: '20px' }}>
                <span style={{ ...bs, fontSize: '12px', fontWeight: 600,
                  color: stock > 5 ? '#16a34a' : stock > 0 ? '#d97706' : '#dc2626' }}>
                  {stock > 5
                    ? `${stock} disponibles`
                    : stock > 0
                    ? `Últimas ${stock} piezas`
                    : 'Sin stock'}
                </span>
              </div>
            )}

            {/* Cantidad */}
            {stock > 0 && selectedVariant && (
              <div style={{ display: 'flex', alignItems: 'center',
                gap: '14px', marginBottom: '20px' }}>
                <label style={{ ...bs, fontSize: '10px', letterSpacing: '0.2em',
                  textTransform: 'uppercase', color: '#999' }}>Cantidad</label>
                <div style={{ display: 'flex', alignItems: 'center',
                  border: '1px solid #e5e0e0', borderRadius: '1px' }}>
                  <button onClick={() => setQuantity(q => Math.max(1, q - 1))}
                    style={{ ...bs, width: '36px', height: '36px', background: 'none',
                      border: 'none', cursor: 'pointer', fontSize: '18px', color: '#888',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'color 0.15s' }}
                    onMouseOver={e => e.currentTarget.style.color = '#961914'}
                    onMouseOut={e => e.currentTarget.style.color = '#888'}>−</button>
                  <span style={{ ...bs, width: '36px', textAlign: 'center',
                    fontSize: '14px', fontWeight: 600, color: '#060D14',
                    borderLeft: '1px solid #eee', borderRight: '1px solid #eee' }}>
                    {quantity}
                  </span>
                  <button onClick={() => setQuantity(q => Math.min(stock, q + 1))}
                    style={{ ...bs, width: '36px', height: '36px', background: 'none',
                      border: 'none', cursor: 'pointer', fontSize: '18px', color: '#888',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'color 0.15s' }}
                    onMouseOver={e => e.currentTarget.style.color = '#961914'}
                    onMouseOut={e => e.currentTarget.style.color = '#888'}>+</button>
                </div>
              </div>
            )}

            {/* Botón agregar al carrito */}
            <button onClick={handleAddToCart}
              disabled={stock === 0 || !selectedVariant || adding}
              style={{ ...bs, width: '100%', padding: '15px',
                background: added ? '#16a34a' : stock === 0 ? '#e5e5e5' : '#961914',
                color: stock === 0 ? '#bbb' : 'white', border: 'none',
                cursor: stock === 0 ? 'not-allowed' : 'pointer',
                fontSize: '11px', fontWeight: 600, letterSpacing: '0.18em',
                textTransform: 'uppercase', transition: 'background 0.3s',
                borderRadius: '1px', marginBottom: '12px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              }}
              onMouseOver={e => { if (stock > 0 && !added) e.currentTarget.style.background = '#7a140f'; }}
              onMouseOut={e => { if (!added) e.currentTarget.style.background = stock === 0 ? '#e5e5e5' : '#961914'; }}>
              {added ? (
                <>
                  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                  </svg>
                  Agregado al carrito
                </>
              ) : adding ? 'Agregando…' : stock === 0 ? 'Sin Stock' : (
                <>
                  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
                  </svg>
                  Agregar al Carrito
                </>
              )}
            </button>

            {/* Reseñas */}
            <div style={{ marginTop: '28px', paddingTop: '24px',
              borderTop: '1px solid #f0eaea' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ ...ds, fontSize: '1.2rem', fontWeight: 600,
                  color: '#060D14', margin: 0 }}>
                  Reseñas ({reviews.length})
                </h3>
                <button onClick={() => setShowForm(v => !v)}
                  style={{ ...bs, background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase',
                    color: '#961914', fontWeight: 600 }}>
                  {showForm ? 'Cancelar' : 'Escribir reseña'}
                </button>
              </div>
              {showForm && (
                <div style={{ marginBottom: '20px' }}>
                  <ProductReviewForm productId={product.id} onSubmit={handleReviewSubmit} />
                </div>
              )}
              <ProductReviewsDisplay reviews={reviews} />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeImg { from{opacity:0} to{opacity:1} }
        @media (max-width: 640px) {
          .product-detail-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default ProductDetailModal;
